import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { purgeApiCacheViaWorker } from '../services/cachePurge';

// Task 4.5: the "switch to this identity and reset whatever's cached for
// the old one" orchestration that used to live inline in
// CanvasDataContext.tsx, extracted into a plain hook (not a context) so
// CanvasDataContext.tsx stays under its line budget. This needs pieces of
// both AppModeContext (guest credentials/mode) and useCourseData (the
// per-course caches being reset) plus AuthContext's switchCanvasUser -- it
// is the composition seam between all three, which is why it isn't itself
// part of either new context: putting it in CanvasSessionContext or
// AppModeContext would create a dependency cycle (each already depends on
// the other, or on this hook's inputs).

interface UseUserSwitchingArgs {
  // 'offline' (task 6.0e) reaches here only so the union matches
  // AppModeContext's Mode. Offline mode does no user switching of its own --
  // the child is chosen when entering the mode, not swapped inside it -- so
  // there is no offline branch below.
  coursesMode: 'demo' | 'guest' | 'auth' | 'offline';
  guestCredentials: any;
  guestAvailableUsers: any[];
  setGuestCredentials: (token: string, userId: string | number | null, rootUrl: string, users?: any[]) => void;
  clearGuestCredentialsMode: () => void;
  resetGuestSelection: () => void;
  switchCanvasUser: (userId: string | number) => Promise<boolean>;
  resetCourseData: () => void;
  loadCourses: () => Promise<void>;
  setLoadingCourses: (value: boolean) => void;
  setLoadingCourseDetails: (value: boolean) => void;
  setLoadingCourseHistory: (value: boolean) => void;
  setError: (value: string) => void;
}

export function useUserSwitching({
  coursesMode,
  guestCredentials,
  guestAvailableUsers,
  setGuestCredentials,
  clearGuestCredentialsMode,
  resetGuestSelection,
  switchCanvasUser,
  resetCourseData,
  loadCourses,
  setLoadingCourses,
  setLoadingCourseDetails,
  setLoadingCourseHistory,
  setError,
}: UseUserSwitchingArgs) {
  const navigate = useNavigate();
  const isGuestMode = coursesMode === 'guest';

  // Returns `true` on success, matching selectCanvasUser's authenticated
  // branch -- that branch's contract is what UserSelectionPrompt reads, and
  // before task 4.5a this function returned undefined, so the guest branch
  // reported failure on every success. Failures still throw rather than
  // returning false: selectCanvasUser's catch converts them, exactly as it
  // does for switchCanvasUser on the authenticated side, so both branches
  // share one error path. This function is internal to this hook -- it is
  // not exposed on any context -- so that catch is its only caller.
  // `selectUserId: string | number` is not a guess: it is the intersection
  // of what this hook's own args already declare -- setGuestCredentials
  // takes `string | number | null` and switchCanvasUser takes
  // `string | number` -- and the `.toString()` call below rules out null.
  const selectGuestCanvasUser = useCallback(async (selectUserId: string | number, shouldNavigate = true) => {
    try {
      if (!guestCredentials) {
        throw new Error('No guest credentials available');
      }
      const selectedUser = guestAvailableUsers.find(user => user.id.toString() === selectUserId.toString());
      if (!selectedUser) {
        throw new Error('Selected user not found in available users list');
      }

      setGuestCredentials(guestCredentials.token, selectUserId, guestCredentials.rootUrl, guestAvailableUsers);
      resetCourseData();

      if (shouldNavigate) {
        navigate('/guest/dashboard');
      }
      console.log('Guest Canvas user selected:', selectedUser.name);
      return true;
    } catch (error) {
      console.error('Error selecting guest Canvas user:', error);
      throw error;
    }
  }, [guestCredentials, guestAvailableUsers, resetCourseData, navigate, setGuestCredentials]);

  // Task 4.5: dropped the old artificial 100ms delay + manual
  // pendingUserSelection dance -- those papered over the pre-TanStack
  // imperative fetch path; with courses/courseDetails keyed reactively off
  // identity, updating guestCredentials can't be observed mid-update.
  const switchGuestUser = useCallback(async (selectUserId: string | number, shouldNavigate = true) => {
    try {
      if (!guestCredentials) {
        throw new Error('No guest credentials available');
      }
      const selectedUser = guestAvailableUsers.find(user => user.id.toString() === selectUserId.toString());
      if (!selectedUser) {
        console.log('Selected user not found in available users list');
        throw new Error('Selected user not found in available users list');
      }

      setLoadingCourses(true);
      setLoadingCourseDetails(true);
      setError('');
      resetCourseData();

      setGuestCredentials(guestCredentials.token, selectUserId, guestCredentials.rootUrl, guestAvailableUsers);

      if (shouldNavigate) {
        navigate('/guest/dashboard');
      }
      console.log('Switched to guest Canvas user:', selectedUser.name);
    } catch (error) {
      console.error('Error switching guest Canvas user:', error);
      setError('Failed to switch user. Please try again.');
      throw error;
    } finally {
      setLoadingCourses(false);
      setLoadingCourseDetails(false);
    }
  }, [guestCredentials, guestAvailableUsers, navigate, resetCourseData, setGuestCredentials, setLoadingCourses, setLoadingCourseDetails, setError]);

  const selectCanvasUser = useCallback(async (selectUserId: string | number, shouldNavigate = true) => {
    try {
      if (isGuestMode) {
        return await selectGuestCanvasUser(selectUserId, shouldNavigate);
      }
      await switchCanvasUser(selectUserId);
      resetCourseData();
      setError('');
      if (shouldNavigate) {
        navigate('/dashboard');
      }
      return true;
    } catch (err) {
      console.error('Error selecting Canvas user:', err);
      setError('Failed to select Canvas user');
      return false;
    }
  }, [switchCanvasUser, resetCourseData, isGuestMode, selectGuestCanvasUser, navigate, setError]);

  const refreshAllData = useCallback(async () => {
    try {
      resetCourseData();
      await loadCourses();
      return true;
    } catch (error) {
      console.error('Error during full data refresh:', error);
      setError('Failed to refresh data for the selected user');
      return false;
    }
  }, [resetCourseData, loadCourses, setError]);

  // Exit guest mode: clear the mode/credentials (AppModeContext) plus this
  // context's own per-course cache and error, which AppModeContext can't
  // reach.
  const clearGuestCredentials = useCallback(async () => {
    clearGuestCredentialsMode();
    resetCourseData();
    setError('');
    // Task 5.3c: leaving guest mode counts as a sign-out. `/guest/courses`
    // and `/guest/snapshots` are cached under exactly the same rules as the
    // authenticated reads, guest credentials are a Canvas API token pasted
    // in by hand, and guest mode is the flow most likely to be used on a
    // borrowed device -- so the exposure, and the one-refetch cost of
    // clearing it, are identical to the authenticated case. Awaited so a
    // caller that navigates afterwards cannot race it.
    await purgeApiCacheViaWorker();
  }, [clearGuestCredentialsMode, resetCourseData, setError]);

  // Comprehensive reset for logout: this hook's data plus a full guest
  // reset (including guestAvailableUsers/pendingUserSelection, unlike
  // clearGuestCredentials above).
  const clearAllCanvasData = useCallback(() => {
    resetCourseData();
    setLoadingCourses(false);
    setLoadingCourseDetails(false);
    setLoadingCourseHistory(false);
    setError('');
    resetGuestSelection();
    console.log('All Canvas data cleared for logout');
  }, [resetCourseData, resetGuestSelection, setLoadingCourses, setLoadingCourseDetails, setLoadingCourseHistory, setError]);

  return {
    selectCanvasUser,
    selectGuestCanvasUser,
    switchGuestUser,
    refreshAllData,
    clearGuestCredentials,
    clearAllCanvasData,
  };
}
